// Clés de stockage sécurisées
const STORAGE_KEYS = {
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    USER: 'cached_user',
    STATE: 'auth_state',
    CODE_VERIFIER: 'code_verifier',
    USER_TYPE: 'auth_user_type',
    APP_ID: 'auth_app_id',
    RETURN_TO: 'auth_return_to',
  } as const;

// 🔐 Liste des clés d'authentification Auth0
const AUTH0_KEYS = [
  'auth0_token',
  'auth0_id_token',
  'auth0_refresh_token',
  'auth0_expires_in',
  'auth0_token_type',
  'auth0_user',
  'auth0_state',
  'auth0_code_verifier',
  'auth0_error',
  'token_refresh_in_progress',
];

// 🔐 Vérifier si le token est potentiellement compromis
function isTokenSuspicious(token: string): boolean {
  if (!token || typeof token !== 'string') return true;
  
  // Vérifier la longueur minimale d'un JWT
  if (token.length < 100) return true;
  
  // Vérifier le format JWT (3 parties séparées par des points)
  const parts = token.split('.');
  if (parts.length !== 3) return true;
  
  // Vérifier les caractères autorisés (base64url)
  const base64UrlPattern = /^[A-Za-z0-9_-]+$/;
  for (const part of parts) {
    if (!base64UrlPattern.test(part)) return true;
  }
  
  return false;
}
  
  // Wrapper sécurisé pour localStorage
  export const secureStorage = {
    // Stockage avec encryption si nécessaire
    setItem(key: keyof typeof STORAGE_KEYS, value: string) {
      try {
        localStorage.setItem(STORAGE_KEYS[key], value);
      } catch (error) {
        console.error(`Error storing ${key}:`, error);
      }
    },
  
    // Récupération avec décryption si nécessaire  
    getItem(key: keyof typeof STORAGE_KEYS): string | null {
      try {
        return localStorage.getItem(STORAGE_KEYS[key]);
      } catch (error) {
        console.error(`Error retrieving ${key}:`, error);
        return null;
      }
    },
  
    // Suppression sécurisée
    removeItem(key: keyof typeof STORAGE_KEYS) {
      try {
        localStorage.removeItem(STORAGE_KEYS[key]);
      } catch (error) {
        console.error(`Error removing ${key}:`, error);
      }
    },
  
    // Nettoyage complet
    clear() {
      try {
        Object.values(STORAGE_KEYS).forEach(key => {
          localStorage.removeItem(key);
        });
      } catch (error) {
        console.error('Error clearing storage:', error);
      }
    }
  };
  
  // Session storage sécurisé
  export const secureSessionStorage = {
    setItem(key: keyof typeof STORAGE_KEYS, value: string) {
      try {
        sessionStorage.setItem(STORAGE_KEYS[key], value);
      } catch (error) {
        console.error(`Error storing ${key} in session:`, error);
      }
    },
  
    getItem(key: keyof typeof STORAGE_KEYS): string | null {
      try {
        return sessionStorage.getItem(STORAGE_KEYS[key]);
      } catch (error) {
        console.error(`Error retrieving ${key} from session:`, error);
        return null;
      }
    },
  
    removeItem(key: keyof typeof STORAGE_KEYS) {
      try {
        sessionStorage.removeItem(STORAGE_KEYS[key]);
      } catch (error) {
        console.error(`Error removing ${key} from session:`, error);
      }
    },
  
    clear() {
      try {
        sessionStorage.clear();
      } catch (error) {
        console.error('Error clearing session storage:', error);
      }
    }
  };

  /**
   * 🔐 Récupère le token d'authentification de manière sécurisée
   * - Vérifie le format du token
   * - Détecte les tokens potentiellement compromis
   */
  export function getToken(): string | null {
    // Priorité aux tokens Auth0 stockés par le callback
    const auth0Token = localStorage.getItem('auth0_token');
    if (auth0Token) {
      // 🔐 Vérifier si le token semble valide
      if (isTokenSuspicious(auth0Token)) {
        console.warn('🚨 Token suspect détecté, suppression...');
        clearAllAuthData();
        return null;
      }
      return auth0Token;
    }
    
    // Fallback vers l'ancien système si nécessaire
    const legacyToken = secureStorage.getItem('ACCESS_TOKEN');
    if (legacyToken && isTokenSuspicious(legacyToken)) {
      console.warn('🚨 Token legacy suspect détecté, suppression...');
      secureStorage.removeItem('ACCESS_TOKEN');
      return null;
    }
    
    return legacyToken;
  }

  /**
   * 🔐 Efface toutes les données d'authentification (logout sécurisé)
   * Utilisé en cas de token compromis ou lors du logout
   */
  export function clearAllAuthData(): void {
    // Effacer les clés Auth0
    AUTH0_KEYS.forEach(key => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (e) {
        // Ignorer les erreurs
      }
    });
    
    // Effacer les clés legacy
    secureStorage.clear();
    
    // Effacer aussi les données de session sensibles
    try {
      sessionStorage.removeItem('auth0_redirect_after_login');
      sessionStorage.removeItem('auth0_just_logged_in');
      sessionStorage.removeItem('auth0_first_time_user');
    } catch (e) {
      // Ignorer
    }
    
    console.log('🔐 Toutes les données d\'authentification ont été effacées');
  }

  /**
   * 🔐 Vérifie si l'utilisateur est dans un état d'authentification valide
   */
  export function isAuthStateValid(): boolean {
    const token = getToken();
    if (!token) return false;
    
    // Vérifier que le token n'est pas expiré
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      
      if (!exp) return false;
      
      // Le token est-il expiré? (avec 60 secondes de marge)
      const now = Math.floor(Date.now() / 1000);
      if (exp < now + 60) {
        console.warn('🚨 Token expiré ou sur le point d\'expirer');
        return false;
      }
      
      return true;
    } catch (e) {
      console.error('Erreur lors de la vérification du token:', e);
      return false;
    }
  }